import React, { useEffect, useState } from 'react';
import Swal from '../librarianSwal';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Constant/Auth';
import { useActiveLibraryId, useSectionBase } from '../../Constant/useActiveLibrary';
import SearchableSelect from '../../shared/SearchableSelect';

import {
  getMembersByLibrary,
  getIssuesByLibrary,
  addFine,
  getErrorMessage
} from '../../api/api';
import '../lib-page-header.css';

const FINE_TYPE_OPTIONS = [
  { value: 'late', label: 'Late' },
  { value: 'damage', label: 'Damage' },
  { value: 'lost', label: 'Lost' },
  { value: 'other', label: 'Other' }
];

const CreateFine = () => {
  const navigate = useNavigate();
  const sectionBase = useSectionBase();
  const { user } = useAuth();

  const libraryId = useActiveLibraryId();
  const staffId = user?.id || user?.staff_id;

  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [issues, setIssues] = useState([]);

  const [formData, setFormData] = useState({
    member_id: '',
    issue_id: '',
    fine_type: 'late',
    amount: '',
    paid_amount: 0,
    balance_amount: '',
    fine_status: 'unpaid',
    reason: '',
    remarks: ''
  });

  const fetchData = async () => {
    try {
      if (!libraryId) return;

      const [memberData, issueData] = await Promise.all([
        getMembersByLibrary(libraryId),
        getIssuesByLibrary(libraryId)
      ]);

      setMembers(memberData || []);
      setIssues(issueData || []);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'amount') {
      setFormData((prev) => ({
        ...prev,
        amount: value,
        balance_amount: value
      }));
      return;
    }

    if (name === 'issue_id') {
      const selectedIssue = issues.find((item) => String(item.id) === String(value));

      setFormData((prev) => ({
        ...prev,
        issue_id: value,
        member_id: selectedIssue?.member_id || prev.member_id
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!libraryId || !formData.member_id || !formData.amount) {
      Swal.fire('Error', 'Member and amount are required', 'error');
      return;
    }

    const payload = {
      library_id: libraryId,
      member_id: formData.member_id,
      issue_id: formData.issue_id || null,
      fine_type: formData.fine_type,
      amount: formData.amount,
      paid_amount: 0,
      balance_amount: formData.amount,
      fine_status: 'unpaid',
      reason: formData.reason || null,
      remarks: formData.remarks || null,
      created_by_staff_id: staffId
    };

    try {
      setLoading(true);
      await addFine(payload);

      Swal.fire('Success', 'Fine created successfully', 'success');
      navigate(`${sectionBase}/fines`);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid mt-3">
      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-plus-circle" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Create Fine</h3>
            <div className="lib-page-header__subtitle">Create manual fine for member</div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`${sectionBase}/fines`)}
          >
            Back
          </button>
        </div>
      </div>

      <div className="card shadow-sm mb-3 table-full-card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Issue / Book</label>
                <SearchableSelect
                  name="issue_id"
                  value={formData.issue_id}
                  onChange={handleChange}
                  options={issues.map((item) => ({
                    value: item.id,
                    label: `${item.title || item.book_title || 'Book'} - ${item.member_name || item.parent_name || 'Member'}`
                  }))}
                  placeholder="Select Issue"
                />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Member *</label>
                <SearchableSelect
                  name="member_id"
                  value={formData.member_id}
                  onChange={handleChange}
                  options={members.map((member) => ({
                    value: member.id,
                    label: `${member.full_name || member.member_name || member.parent_name || 'Member'}${member.mobile ? ` - ${member.mobile}` : ''}`
                  }))}
                  placeholder="Select Member"
                  required
                />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Fine Type</label>
                <SearchableSelect
                  name="fine_type"
                  value={formData.fine_type}
                  onChange={handleChange}
                  options={FINE_TYPE_OPTIONS}
                  clearable={false}
                />
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Amount *</label>
                <input
                  type="number"
                  name="amount"
                  className="form-control"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-8 mb-3">
                <label className="form-label">Reason</label>
                <input
                  type="text"
                  name="reason"
                  className="form-control"
                  value={formData.reason}
                  onChange={handleChange}
                  placeholder="Fine reason"
                />
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label">Remarks</label>
                <textarea
                  name="remarks"
                  rows="3"
                  className="form-control"
                  value={formData.remarks}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="text-end">
              <button
                type="button"
                className="btn btn-light me-2"
                onClick={() => navigate(`${sectionBase}/fines`)}
              >
                Cancel
              </button>

              <button className="btn btn-primary" disabled={loading}>
                {loading ? 'Saving...' : 'Save Fine'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateFine;